import { useEffect, useState } from "react";
import { IMyPortfolio } from "../../../../interface/myPortfolio";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatCurrency, getCurrentFinancialYear } from "../../../../utils/commonUtil";
import RentEditForm from "../../MyCashFlow/Rent/EditRent";
interface ICashFlowRent {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  type: string;
  annualRent: number;
  weeklyRent: number;
  leaseStart: string | null | undefined;
  leaseEnd: string | null | undefined;
  nextRentReview: string | null | undefined;
  tenants: string;
}

const CashFlowRent: React.FC<{
  cashFlowData: IMyPortfolio;
  year: string;
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
  isShowingTotal?: boolean;
}> = ({ cashFlowData, year, isEditable, setIsEditable, isShowingTotal }) => {
  const [transformedData, setTransformedData] = useState<ICashFlowRent[]>([]);
  const currentYear = "20" + Number(getCurrentFinancialYear()).toString();

  useEffect(() => {
    if (cashFlowData) {
      const actual = isShowingTotal
        ? cashFlowData.CashflowRent?.Actuals?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowRentPortion?.Actuals?.find((item: any) => item.Year === year) || {};
      const forecast = isShowingTotal
        ? cashFlowData.CashflowRent?.Forecast?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowRentPortion?.Forecast?.find((item: any) => item.Year === year) || {};
      const variance = isShowingTotal
        ? cashFlowData.CashflowRent?.Variance?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowRentPortion?.Variance?.find((item: any) => item.Year === year) || {};

      setTransformedData(
        [
          { type: "Forecast", data: forecast },
          { type: "Actuals", data: actual },
          { type: "Variance", data: variance },
        ].map(({ type, data }: { type: string; data: any }) => ({
          street: cashFlowData.PortfolioTrackerDetailLabel.StreetAddress || "",
          suburb: cashFlowData.PortfolioTrackerDetailLabel.Suburb || "",
          state: cashFlowData.PortfolioTrackerDetailLabel.State || "",
          postcode: cashFlowData.PortfolioTrackerDetailLabel?.Postcode || "",
          country: cashFlowData.PortfolioTrackerDetailLabel?.Country || "",
          type,
          annualRent: Number(data?.RentPA) || 0,
          weeklyRent: Number(data?.RentPW) || 0,
          leaseStart: data?.LeaseStart || "",
          leaseEnd: data?.LeaseEnd,
          nextRentReview: data?.NextReview,
          tenants: data.Tenants || "",
        }))
      );
    }
  }, [cashFlowData, year, isShowingTotal]);

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatCurrency(-value)})` : formatCurrency(value)}
    </span>
  );

  const onCancel = () => {
    setIsEditable(false);
  };

  return (
    <>
      {isEditable ? (
        <>
          <RentEditForm
            cashFlowData={undefined}
            portfolioTrackerId={cashFlowData.PortfolioTrackerDetailLabel.PortfolioTrackerId}
            propertyName={`${cashFlowData.PortfolioTrackerDetailLabel.StreetAddress}, ${cashFlowData.PortfolioTrackerDetailLabel.Suburb}, ${cashFlowData.PortfolioTrackerDetailLabel.State}, ${cashFlowData.PortfolioTrackerDetailLabel.Postcode} (${cashFlowData.PortfolioTrackerDetailLabel.Country})`}
            setIsEditable={setIsEditable}
            onCancel={onCancel}
          />
        </>
      ) : (
        <div className="w-full">
          {transformedData && (
            <DataTable value={transformedData} rowGroupMode="rowspan" groupRowsBy="property" size="small">
              <Column
                field="property"
                header="Address/property name"
                body={(rowData) => (
                  <div className="flex items-center">
                    <span>{`${rowData.street}, ${rowData.suburb}, ${rowData.state}, ${rowData.postcode} (${rowData.country})`}</span>
                  </div>
                )}
              />
              <Column field="type" header="Type" body={(rowData) => rowData.type} />
              <Column
                field="annualRent"
                header="Annual rent"
                body={(rowData) =>
                  Number(currentYear) >= Number(year) || rowData.type == "Forecast"
                    ? currencyTemplate(rowData.annualRent)
                    : ""
                }
              />
              <Column
                field="weeklyRent"
                header="Weekly rent"
                body={(rowData) =>
                  Number(currentYear) >= Number(year) || rowData.type == "Forecast"
                    ? currencyTemplate(rowData.weeklyRent)
                    : ""
                }
              />
              <Column
                field="leaseStart"
                header="Lease start"
                body={(rowData) =>
                  Number(currentYear) >= Number(year) || rowData.type == "Forecast" ? rowData.leaseStart : ""
                }
              />
              <Column
                field="leaseEnd"
                header="Lease end"
                body={(rowData) =>
                  Number(currentYear) >= Number(year) || rowData.type == "Forecast" ? rowData.leaseEnd : ""
                }
              />
              <Column
                field="nextRentReview"
                header="Next rent review"
                body={(rowData) =>
                  Number(currentYear) >= Number(year) || rowData.type == "Forecast" ? rowData.nextRentReview : ""
                }
              />
              <Column
                field="tenants"
                header="Tenants"
                body={(rowData) =>
                  Number(currentYear) >= Number(year) || rowData.type == "Forecast" ? rowData.tenants : ""
                }
              />
            </DataTable>
          )}
        </div>
      )}
    </>
  );
};

export default CashFlowRent;
